import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type { Permission } from '@/shell/module-system'

/**
 * Vendor document requests (2026-10-12): regulatory documents for products
 * already set up, requested through the secure vendor link. Each request
 * owns a card on the Regulatory vendor board. Backend:
 * middleware/app/api/routes/vendor_doc_requests.py.
 */

export type DocRequestStatus = 'open' | 'vendor_submitted' | 'completed' | 'revoked'
export type DocState = 'missing' | 'pending' | 'attached' | 'not_available' | 'rejected'

export interface DocRequestUpload {
  id: number
  product_tmpl_id: number | null
  doc_type_key: string
  doc_type_label: string
  document_id: number | null
  original_filename: string | null
  issued_on: string | null
  expires_on: string | null
  not_available_reason: string | null
  review_status: 'pending' | 'accepted' | 'rejected'
  review_note: string | null
  reviewed_by: string | null
  attached_to: { product_tmpl_id: number; product_document_id: number }[]
  requested: boolean
  created_at: string | null
}

export interface DocRequestDetail {
  id: string
  reference: string
  status: DocRequestStatus
  vendor_partner_id: number
  vendor_name: string
  contact_name: string | null
  contact_email: string | null
  message_to_vendor: string | null
  expires_at: string
  link_active: boolean
  submitted_at: string | null
  attestation: { name: string; title: string; note?: string } | null
  created_by: string
  created_at: string | null
  regulatory_request_id: string | null
  progress: { settled: number; total: number }
  products: {
    product_tmpl_id: number
    name: string
    default_code: string | null
    documents: { doc_type_key: string; label: string; state: DocState }[]
  }[]
  uploads: DocRequestUpload[]
  events: { event: string; actor: string | null; at: string; detail: Record<string, unknown> | null }[]
}

/** Board badge (on each card in the list response). */
export interface DocRequestSummary {
  id: string
  reference: string
  status: DocRequestStatus
  settled: number
  total: number
}

export interface ProductCoverage {
  product_tmpl_id: number
  name: string
  default_code: string | null
  current: Record<string, { expires_on: string | null }>
  missing_required: string[]
  expiring: string[]
}

export interface CreateDocRequestBody {
  vendor_partner_id: number
  products: { product_tmpl_id: number; doc_types: string[] }[]
  contact_name?: string | null
  contact_email?: string | null
  message_to_vendor?: string | null
  days?: number | null
  regulatory_request_id?: string | null
}

export interface LinkResponse {
  request: DocRequestDetail
  link: string
  card_id: string | null
  suggested_email: string
  suggested_subject: string
}

export const DOC_REQUEST_STATUS_LABEL: Record<DocRequestStatus, string> = {
  open: 'Waiting for vendor',
  vendor_submitted: 'Docs received',
  completed: 'Completed',
  revoked: 'Cancelled',
}

/** Who can create / resend a request (sales included) vs. attach files. */
export const DOC_REQUEST_CREATE_ROLES: readonly Permission[] = ['admin', 'operations', 'operations_logistics', 'purchasing', 'regulatory', 'sales']
export const DOC_REQUEST_REVIEW_ROLES: readonly Permission[] = ['admin', 'operations', 'operations_logistics', 'purchasing', 'regulatory']

const KEY = ['vendor-doc-requests'] as const
const BASE = '/api/vendor-doc-requests'

export function useVendorProducts(partnerId: number | null) {
  return useQuery({
    queryKey: [...KEY, 'vendor-products', partnerId],
    queryFn: () => jsonFetch<{ items: ProductCoverage[]; total: number }>(`${BASE}/vendor-products/${partnerId}`),
    enabled: partnerId != null,
    staleTime: 60_000,
  })
}

export function useProductCoverage(ids: number[]) {
  const key = [...ids].sort((a, b) => a - b).join(',')
  return useQuery({
    queryKey: [...KEY, 'coverage', key],
    queryFn: () => jsonFetch<{ items: ProductCoverage[] }>(`${BASE}/coverage?ids=${key}`),
    enabled: ids.length > 0,
    staleTime: 60_000,
  })
}

export function useDocRequestForCard(cardId: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'by-card', cardId],
    queryFn: () => jsonFetch<{ request: DocRequestDetail | null }>(`${BASE}/by-card/${cardId}`),
    enabled: !!cardId,
    staleTime: 15_000,
  })
}

export function useDocRequestsBadge(enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'badge'],
    queryFn: () => jsonFetch<{ docs_received: number }>(`${BASE}/badge`),
    enabled,
    staleTime: 60_000,
    refetchInterval: 120_000,
  })
}

function useInvalidate() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: KEY })
    void qc.invalidateQueries({ queryKey: ['regulatory'] })
  }
}

export function useCreateDocRequest() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (body: CreateDocRequestBody) => jsonFetch<LinkResponse>(BASE, { method: 'POST', body }),
    onSuccess: invalidate,
  })
}

export function useDocRequestAction() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ id, action, body }: {
      id: string
      action: 'new-link' | 'reopen' | 'revoke'
      body?: Record<string, unknown>
    }) => jsonFetch<LinkResponse | DocRequestDetail>(`${BASE}/${id}/${action}`, { method: 'POST', body: body ?? {} }),
    onSuccess: invalidate,
  })
}

export function useReviewUpload() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ id, uploadId, action, body }: {
      id: string
      uploadId: number
      action: 'attach' | 'reject'
      body: Record<string, unknown>
    }) => jsonFetch<DocRequestDetail>(`${BASE}/${id}/uploads/${uploadId}/${action}`, { method: 'POST', body }),
    onSuccess: invalidate,
  })
}

export interface RequestableType {
  key: string
  label: string
  vendor_requestable: boolean
  active: boolean
}

/** Document types (product document library); only vendor-requestable ones are offered. */
export function useRequestableTypes() {
  return useQuery({
    queryKey: ['product-documents', 'types', false],
    queryFn: () => jsonFetch<RequestableType[]>('/api/product-documents/types'),
    staleTime: 60_000,
    select: (types) => types.filter((t) => t.vendor_requestable && t.active),
  })
}

/** What to pre-tick for a product: required documents it lacks, plus anything expired or expiring soon. */
export function suggestedTypes(c: ProductCoverage | undefined, requestable: Set<string>): string[] {
  if (!c) return []
  return [...new Set([...c.missing_required, ...c.expiring])].filter((k) => requestable.has(k))
}
